'use client';

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ArrowLeftRight, Wallet } from 'lucide-react';
import { CoinIcon, SokoCoinIcon } from '@/app/components/SvgIcons';
import { User } from '@/types/user';

interface WalletDropdownProps {
    user: User;
    onOpenExchange: () => void;
}

type WalletCurrency = 'points' | 'soko_points';

const STORAGE_KEY = 'soko_wallet_primary_currency';

export default function WalletDropdown({ user, onOpenExchange }: WalletDropdownProps) {
    const [open, setOpen] = useState(false);
    const [primaryCurrency, setPrimaryCurrency] = useState<WalletCurrency>(() => {
        if (typeof window === 'undefined') return 'points';
        return localStorage.getItem(STORAGE_KEY) === 'soko_points' ? 'soko_points' : 'points';
    });
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;

        const onClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    const selectCurrency = (currency: WalletCurrency) => {
        setPrimaryCurrency(currency);
        localStorage.setItem(STORAGE_KEY, currency);
        setOpen(false);
    };

    const primaryBalance = primaryCurrency === 'soko_points' ? (user.soko_points ?? 0) : user.points;

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                aria-label="Wallet balances"
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 rounded-xl text-gray-200 hover:bg-gray-800/70 transition-colors"
            >
                <Wallet size={16} className="text-emerald-400" />
                <span className="font-semibold">{primaryBalance.toLocaleString()}</span>
                {primaryCurrency === 'soko_points' ? <SokoCoinIcon size={15} /> : <CoinIcon size={15} />}
                <ChevronDown
                    size={14}
                    className={`text-gray-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {open && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-40 bg-gray-900 border border-gray-800 rounded-xl shadow-2xl overflow-hidden z-50">
                    <button
                        onClick={() => selectCurrency('points')}
                        aria-label="Show Kick Points balance"
                        className={`w-full flex items-center gap-2 px-4 py-2 border-b border-gray-800 transition-colors ${
                            primaryCurrency === 'points' ? 'bg-gray-800/60' : 'hover:bg-gray-800/40'
                        }`}
                    >
                        <CoinIcon size={16} />
                        <span className="font-bold text-green-400">{user.points.toLocaleString()}</span>
                    </button>

                    <button
                        onClick={() => selectCurrency('soko_points')}
                        aria-label="Show Soko Points balance"
                        className={`w-full flex items-center gap-2 px-4 py-2 border-b border-gray-800 transition-colors ${
                            primaryCurrency === 'soko_points' ? 'bg-gray-800/60' : 'hover:bg-gray-800/40'
                        }`}
                    >
                        <SokoCoinIcon size={16} />
                        <span className="font-bold text-emerald-400">{(user.soko_points ?? 0).toLocaleString()}</span>
                    </button>

                    <button
                        onClick={() => { setOpen(false); onOpenExchange(); }}
                        className="w-full flex items-center justify-center gap-2 py-2 text-sm font-semibold text-emerald-400 hover:bg-emerald-900/20 transition-colors"
                    >
                        <ArrowLeftRight size={15} />
                        Exchange
                    </button>
                </div>
            )}
        </div>
    );
}
